/**
 * The platform-upgrader service, reached through the frontend's nginx at
 * ``/api/platform-upgrader/``. Installs without it answer 404 there.
 *
 * Every rejection is an ``UpgraderError`` carrying the HTTP ``status`` and the
 * service's ``detail`` message, because the UI branches on the status.
 */
import { get_base_url } from '@/shared/http/client';

export class UpgraderError extends Error {
    constructor(status, detail) {
        super(detail || `Request failed: ${status}`);
        this.status = status;
        this.detail = this.message;
    }
}

const upgraderUrl = (path) => `${get_base_url(window.location.origin)}/platform-upgrader/${path}`;

async function request(method, path, body) {
    const response = await fetch(upgraderUrl(path), {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: body === undefined ? undefined : JSON.stringify(body),
        credentials: 'include',
    });
    let payload = null;
    try {
        payload = await response.json();
    } catch {
        payload = null;
    }
    if (!response.ok) {
        const detail = typeof payload?.detail === 'string' ? payload.detail : '';
        throw new UpgraderError(response.status, detail);
    }
    return payload;
}

/**
 * GET status → running version, the offer and its releases, ``in_app_upgrade``, ``schedule``, the ``upgrade``
 * under way and the last ``failure``.
 */
export const fetchStatus = () => request('GET', 'status');

/** Server-sent events, each the whole status: one whenever it changes, for following an update live. */
export const statusStreamUrl = () => upgraderUrl('status/stream');

/** GET me → ``{snoozed, notifications_seen, whats_new}`` for the signed-in user. */
export const fetchMe = () => request('GET', 'me');

/** POST me/snooze → hide the popup until a newer version is offered. */
export const snoozeOffer = () => request('POST', 'me/snooze');

/** POST me/notifications/seen → clear the bell's count until a newer offer or a changed schedule. */
export const markNotificationsSeen = () => request('POST', 'me/notifications/seen');

/** POST me/whats-new/seen → stop showing What's new for the running version. */
export const markWhatsNewSeen = () => request('POST', 'me/whats-new/seen');

/** POST schedule ``{at}`` (Admin) → the schedule. ``at`` is an ISO string with a time zone. */
export const scheduleUpgrade = (at) => request('POST', 'schedule', { at });

/** DELETE schedule (Admin). */
export const cancelScheduledUpgrade = () => request('DELETE', 'schedule');

/**
 * GET busy (Admin) → ``{busy, processes, checked_at}``: the work an update would wait for. Each process has
 * ``kind``, ``name``, ``started_by``, ``started_at`` and ``blocking``; non-blocking ones continue after the update.
 */
export const fetchBusy = () => request('GET', 'busy');

/**
 * POST upgrade (Admin) → ``{upgrade, processes}``. While work runs the update waits for it (phase ``draining``)
 * and new work is refused; ``force`` skips the wait, also for an update that is already waiting.
 */
export const startUpgrade = ({ force = false } = {}) => request('POST', 'upgrade', { force });

/** DELETE upgrade (Admin) → cancel an update that is still waiting, so new work can start again. */
export const cancelUpgrade = () => request('DELETE', 'upgrade');
